import { ClientError } from 'graphql-request';

import { ProductType } from 'src/common/interfaces/Tenant';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';

import { buildMobileScansListFallbackVariables } from './helpers/buildFallbackVariables';
import { buildMobileScansListVariables } from './helpers/buildVariables';
import { transformMobileScansListFallbackResults } from './helpers/transformFallbackResults';
import { transformMobileScansListResults } from './helpers/transformResults';
import { MOBILE_SCANS_LIST_ENTITY_NAME } from './MobileScansList.constants';
import { MFA_SCANS_LIST_QUERY, MSDK_SCANS_LIST_QUERY } from './MobileScansList.query';
import {
	IGetMobileScansListParams,
	IMobileScansListFallbackQuery,
	IMobileScansListQuery,
	IMobileScansListResult,
	IMobileScansListRow,
	MobileScansProductType
} from './MobileScansList.types';
import { MOBILE_SCANS_LIST_FALLBACK_QUERY } from './MobileScansListFallback.query';
import { MobileScansScreenshotsQuery } from './MobileScansScreenshots.generated';
import { MobileScansScreenshots } from './MobileScansScreenshots.gql';

const buildScreenshotsByScanId = (result: MobileScansScreenshotsQuery): Map<string, string> => {
	const map = new Map<string, string>();
	result.issues?.forEach((issue) => {
		if (issue?.scan_id && issue.screenshot && !map.has(issue.scan_id)) {
			map.set(issue.scan_id, issue.screenshot);
		}
	});
	return map;
};

const fetchScreenshotsByScanId = async (runId: string): Promise<Map<string, string>> => {
	const result = await gqlClient.request<MobileScansScreenshotsQuery>(MobileScansScreenshots, {
		runId: { value: [runId] }
	});
	return buildScreenshotsByScanId(result);
};

const enrichWithScreenshots = async (
	result: IMobileScansListResult,
	runId: string
): Promise<IMobileScansListResult> => {
	const screenshotMap = await fetchScreenshotsByScanId(runId);
	return {
		...result,
		rows: result.rows.map((row) => ({
			...row,
			scanScreenshotUrl: screenshotMap.get(row.scanId) ?? row.scanScreenshotUrl
		}))
	};
};

const hasAnyScreenshot = (rows: IMobileScansListRow[]): boolean =>
	rows.some((row) => Boolean(row.scanScreenshotUrl));

const SCHEMA_UNAVAILABLE_ERROR_MARKERS = [
	'Cannot query field',
	'Unknown type',
	'Unknown argument',
	'does not exist',
	'not found'
];

const getErrorMessages = (error: unknown): string[] => {
	if (error instanceof ClientError) {
		const responseErrors =
			error.response.errors?.map((responseError) => responseError.message) ?? [];
		return [error.message, ...responseErrors];
	}

	if (error instanceof Error) {
		return [error.message];
	}

	return [];
};

const isMobileScansListSchemaUnavailableError = (error: unknown): boolean =>
	getErrorMessages(error).some(
		(message) =>
			message.includes(MOBILE_SCANS_LIST_ENTITY_NAME) &&
			SCHEMA_UNAVAILABLE_ERROR_MARKERS.some((marker) => message.includes(marker))
	);

const getMobileScansListFallback = async (
	params: IGetMobileScansListParams
): Promise<IMobileScansListResult> => {
	try {
		const variables = buildMobileScansListFallbackVariables(params);
		const result = await gqlClient.request<IMobileScansListFallbackQuery>(
			MOBILE_SCANS_LIST_FALLBACK_QUERY,
			variables as unknown as Record<string, unknown>
		);

		return transformMobileScansListFallbackResults(result, params);
	} catch (error) {
		Logger.error(
			'MobileScansListService -> getMobileScansListFallback failed with an error:',
			error
		);
		throw error;
	}
};

const getQuery = (productName?: MobileScansProductType): string =>
	productName === ProductType.MOBILE_SDK ? MSDK_SCANS_LIST_QUERY : MFA_SCANS_LIST_QUERY;

export const getMobileScansList = async (
	params: IGetMobileScansListParams
): Promise<IMobileScansListResult> => {
	try {
		const variables = buildMobileScansListVariables(params);
		const rawResult = await gqlClient.request<IMobileScansListQuery>(
			getQuery(params.productName),
			variables
		);

		const result = transformMobileScansListResults(rawResult, params.productName);

		if (!hasAnyScreenshot(result.rows)) {
			try {
				return await enrichWithScreenshots(result, params.runId);
			} catch (enrichError) {
				Logger.warn(
					'MobileScansListService -> screenshot enrichment failed, returning without screenshots:',
					enrichError
				);
			}
		}

		return result;
	} catch (error) {
		if (isMobileScansListSchemaUnavailableError(error)) {
			Logger.warn(
				'MobileScansListService -> mobile_scans_list is unavailable, using temporary fallback:',
				error
			);
			return getMobileScansListFallback(params);
		}

		Logger.error('MobileScansListService -> getMobileScansList failed with an error:', error);
		throw error;
	}
};
